import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { UrlParams } from 'types';
import { PaginatedAtRiskReportList } from 'types/generated';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface IUseAthletesAtRisk {
  date: string;
  ordering?: string;
  enabled?: boolean;
  csvExport?: string;
  limit?: string;
}

interface QueryKeyType {
  pageParam?: UrlParams;
  queryKey?: (string | undefined | string[])[];
}

function useAthletesAtRisk({
  date,
  ordering,
  enabled = true,
  csvExport,
  limit = '20',
}: IUseAthletesAtRisk): UseInfiniteQueryResult<AxiosResponse<PaginatedAtRiskReportList>> {
  const fetchAthletesAtRisk = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedAtRiskReportList>> => {
      const params = pageParam;

      params.date = date;

      if (ordering) {
        params.ordering = ordering
          .replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
          .replace('.', () => '__')
          .concat(',pk');
      }

      return client.get(`reports/athletes-at-risk?${encodeUrlParams(params)}`);
    },
    [limit, ordering, date],
  );

  return useInfiniteQuery(['athletes-at-risk', date, ordering, csvExport], fetchAthletesAtRisk, {
    getNextPageParam: page => {
      return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
    },
    enabled: enabled && date !== null,
    staleTime: 86400000, // 1 day
  });
}

export default useAthletesAtRisk;
